import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import type { BackendTypes } from '@tf/api';
import { Box, createModal, ModalFooter, TFNotifier, useModalControl } from '@tf/ui';

import { useDisconnectMutation } from '@/core/api';

export interface RemoveConnectionModalProps {
	parentEntityName: string;
	parentEntityId: BackendTypes.GraphId;
	connectionKind: BackendTypes.EntityConnectionKind;
	entityId: BackendTypes.GraphId;
}

export const RemoveConnectionModal = createModal<RemoveConnectionModalProps>(
	({ parentEntityId, connectionKind, entityId }) => {
		const { _ } = useLingui();
		const { onClose } = useModalControl();

		const disconnectMutation = useDisconnectMutation();

		const handleRemove = () => {
			disconnectMutation.mutate(
				{ parentEntityId, connectionKind, entityId },
				{
					onSuccess: () => {
						TFNotifier.success(
							_(
								msg({
									id: 'removeConnectionModal.notifications.success',
									message: 'Changes saved',
								}),
							),
						);
						onClose();
					},
					onError: () => {
						TFNotifier.error(
							_(
								msg({
									id: 'removeConnectionModal.notifications.fail',
									message: 'Failed to save changes',
								}),
							),
						);
					},
				},
			);
		};

		return (
			<>
				<Box p="16px">
					<Trans id="structure.removeConnectionModal.confirmationNeeded">
						Please note that if this is the last connection to the entity, the entity will also be removed.
					</Trans>
				</Box>

				<ModalFooter
					cancelLabel={_(
						msg({
							id: 'structure.removeConnectionModal.cancelLabel',
							message: `Keep connection`,
						}),
					)}
					submitLabel={_(
						msg({
							id: 'structure.removeConnectionModal.submitLabel',
							message: 'Remove connection',
						}),
					)}
					submitButtonColor="red"
					onCancel={onClose}
					onSubmit={handleRemove}
				/>
			</>
		);
	},
);
